import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { AlertPopup, HttpLoader } from 'app/app.config';
import { CoreService } from 'app/services/core.service';

interface CourseProgress {
  title: string;
  category: string;
  instructor: string;
  progress: number;
  color: string;
}

interface DashboardSession {
  title: string;
  date: string;
  month: string;
  time: string;
  category: string;

}

interface DashboardStat {
  title: string;
  value: () => number;
  icon: string;
  color: string;
  roles: string[];
}

interface MostAskedQuestion {
  question: string;
  percentage?: number;
}

interface UploadedDocument {
  messageId: number | string;
  message: string;
  uploadedAt: string | Date;
}



@Component({
  selector: 'app-dashboard',
  // imports: [],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  standalone: false
})
export class DashboardComponent implements OnInit {

  private readonly coreService = inject(CoreService);

  @Input() currentUser: any;

  // =====================================================
  // DASHBOARD COUNTS
  // =====================================================

  readonly noOfClients = signal(0);
  readonly noOfAllClientUsers = signal(0);
  readonly noOfActiveUsers = signal(0);
  readonly noOfInActiveUsers = signal(0);
  readonly noOfDocuments = signal(0);

  // =====================================================
  // USER PROFILE
  // =====================================================

  readonly userProfile = this.coreService.userProfile;

  isLoggedUserName = 'User';
  userRole = 0;

  selectedActivityPeriod = '7';

  // =====================================================
  // AI METRICS
  // =====================================================

  readonly recentQuestions = signal<MostAskedQuestion[]>([]);
  readonly documents = signal<UploadedDocument[]>([]);

  readonly aiMetrics = signal({
    questions: 0,
    summaries: 0,
    assessments: 0
  });

  // =====================================================
  // DASHBOARD STATISTICS
  // =====================================================

  readonly stats: DashboardStat[] = [
    {
      title: 'All Clients',
      value: this.noOfClients,
      icon: 'bi-people',
      color: 'primary',
      roles: ['1']
    },
    {
      title: 'All Users',
      value: this.noOfAllClientUsers,
      icon: 'bi-people',
      color: 'info',
      roles: ['1', '2']
    },
    {
      title: 'Active Users',
      value: this.noOfActiveUsers,
      icon: 'bi-person-check',
      color: 'success',
      roles: ['1', '2']
    },
    {
      title: 'Inactive Users',
      value: this.noOfInActiveUsers,
      icon: 'bi-person-exclamation',
      color: 'warning',
      roles: ['1', '2']
    },
    {
      title: 'Total Documents',
      value: this.noOfDocuments,
      icon: 'bi-file-earmark-check',
      color: 'purple',
      roles: ['1', '2']
    }
  ];

  readonly isLoggedUserRole = signal<string>('');

  readonly filteredStats = computed(() => {
    const role = this.isLoggedUserRole()
      .trim()
      .replace(/^["']|["']$/g, '');

    return this.stats.filter(stat =>
      stat.roles.includes(role)
    );
  });

  ngOnInit(): void {
    const storedRole = localStorage.getItem('user_role') ?? '';

    this.isLoggedUserRole.set(
      storedRole.trim().replace(/^["']|["']$/g, '')
    );

    console.log('Stored role:', JSON.stringify(storedRole));
    console.log('Normalized role:', this.isLoggedUserRole());
    console.log('Filtered stats:', this.filteredStats());

    this.isLoggedUserName = this.getUserName();
    this.loadDashboard();
  }

  // =====================================================
  // LOAD DASHBOARD
  // =====================================================

  private loadDashboard(): void {
    this.getClientRecord();
    this.getTotalUsers();
    this.getTotalDocuments();
    this.setMostAskQuestions();

    this.loadDocuments();
  }

  // =====================================================
  // USER NAME
  // =====================================================

  getUserName(): string {
    const profile = this.coreService.userProfile();

    this.userRole = Number(profile?.user_role ?? 0);

    const firstName =
      profile?.firstName ??
      profile?.first_name ??
      '';

    const lastName =
      profile?.lastName ??
      profile?.last_name ??
      '';

    const fullName = `${firstName} ${lastName}`.trim();

    return (
      fullName ||
      this.currentUser?.name ||
      this.currentUser?.userName ||
      'User'
    );
  }

  // =====================================================
  // CLIENT COUNT
  // =====================================================

  getClientRecord(): void {
    this.coreService.getClients().subscribe({
      next: (response: any) => {
        this.noOfClients.set(
          Number(response?.count ?? 0)
        );
      },
      error: (error) => {
        console.error('Get clients error:', error);
        this.noOfClients.set(0);
      }
    });
  }

  // =====================================================
  // USER COUNTS
  // =====================================================

  getTotalUsers(): void {
    this.coreService.getAllUsersCount().subscribe({
      next: (response: any) => {
        const data = response?.data ?? {};

        this.noOfAllClientUsers.set(
          Number(data?.total_users ?? 0)
        );

        this.noOfActiveUsers.set(
          Number(data?.active_users ?? 0)
        );

        this.noOfInActiveUsers.set(
          Number(
            data?.inactive_users ??
            data?.in_active_users ??
            Math.max(
              0,
              Number(data?.total_users ?? 0) -
              Number(data?.active_users ?? 0)
            )
          )
        );
      },
      error: (error) => {
        console.error('Get total users error:', error);

        this.noOfAllClientUsers.set(0);
        this.noOfActiveUsers.set(0);
        this.noOfInActiveUsers.set(0);
      }
    });
  }

  // =====================================================
  // DOCUMENT COUNT
  // =====================================================

  getTotalDocuments(): void {
    this.coreService.getDocumentCount().subscribe({
      next: (response: any) => {
        this.noOfDocuments.set(
          Number(response?.data?.total_documents ?? 0)
        );

        console.log(response,'response:::::::::response');
        
      },
      error: (error) => {
        console.error('Get total documents error:', error);
        this.noOfDocuments.set(0);
      }
    });
  }

  // =====================================================
  // MOST ASKED QUESTIONS
  // =====================================================

  setMostAskQuestions(): void {
    this.coreService.getMostAskQuestions().subscribe({
      next: (response: { data?: MostAskedQuestion[] }) => {
        this.recentQuestions.set(response?.data ?? []);

        console.log(this.recentQuestions);

      },
      error: (error) => {
        console.error(
          'Failed to load most asked questions:',
          error
        );

        this.recentQuestions.set([]);
      }
    });
  }

  // =====================================================
  // PROGRESS HELPERS
  // =====================================================

  getProgressClass(progress: number): string {
    if (progress >= 80) {
      return 'high';
    }

    if (progress >= 50) {
      return 'medium';
    }

    return 'low';
  }

  // =====================================================
  // NAVIGATION ACTIONS
  // =====================================================

  onViewCourses(): void {
    // Add Router navigation when the courses route is available.
  }

  onViewSessions(): void {
    // Add Router navigation when the sessions route is available.
  }

  loadDocuments(): void {
    this.coreService.getUploadDocumentList().subscribe({
      next: (response: any) => {
        const data: UploadedDocument[] = response?.data ?? [];

        this.documents.set(
          [...data].sort(
            (a, b) =>
              new Date(b.uploadedAt).getTime() -
              new Date(a.uploadedAt).getTime()
          )
        );
      },
      error: (error) => {
        console.error('Failed to load documents:', error);
        this.documents.set([]);
      }
    });
  }
}
